/**
 * PWA service worker registration.
 *
 * Runs only in production builds so the dev loop isn't confused by a
 * cached stale bundle. Exposes a tiny pub-sub so the install prompt can
 * listen for `beforeinstallprompt` deferrals and a "new version ready"
 * nudge.
 */

const listeners = {
  installAvailable: new Set(),
  installed: new Set(),
  updateAvailable: new Set(),
};

export function onPwaEvent(name, handler) {
  if (!listeners[name]) return () => {};
  listeners[name].add(handler);
  return () => listeners[name].delete(handler);
}

function emit(name, payload) {
  if (!listeners[name]) return;
  listeners[name].forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      // Swallow so one bad listener doesn't break the others.
      // eslint-disable-next-line no-console
      console.warn('[pwa] listener error', err);
    }
  });
}

let deferredPrompt = null;

export function getDeferredInstallPrompt() {
  return deferredPrompt;
}

export async function triggerInstall() {
  if (!deferredPrompt) return 'unavailable';
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice.outcome; // 'accepted' | 'dismissed'
}

export function registerPwa() {
  if (typeof window === 'undefined') return;

  // Listen for the install prompt event regardless of environment, so we
  // can surface the prompt in preview too (helps QA).
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    emit('installAvailable', event);
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit('installed');
  });

  // Register the service worker only in production builds to avoid the
  // dev-server HMR fighting the cache.
  const isProd = process.env.NODE_ENV === 'production';
  if (!isProd) return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => {
        // If a new SW is already sitting in `waiting` at registration time
        // (i.e. user reopened the tab after a deploy), surface the toast
        // immediately without waiting for a fresh update cycle.
        if (reg.waiting && navigator.serviceWorker.controller) {
          emit('updateAvailable', reg);
        }

        reg.onupdatefound = () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.onstatechange = () => {
            if (
              installing.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              // A newer SW is waiting. Tell listeners; they can show a
              // "Refresh for latest" toast if desired.
              emit('updateAvailable', reg);
            }
          };
        };

        // Poll for updates every 30 mins so long-lived tabs eventually
        // discover a deploy without a manual reload.
        setInterval(() => {
          reg.update().catch(() => null);
        }, 30 * 60 * 1000);
      })
      .catch(() => {
        // Silent failure is fine, the app still works without SW.
      });

    // When the new SW takes control, reload so the page runs against the
    // fresh bundles. Only reload if the user explicitly triggered the
    // update (via applyPwaUpdate) — otherwise first-time SW registration
    // would loop, especially with SSR's SW cleanup script in the mix.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!userTriggeredUpdate) return;
      window.location.reload();
    });
  });
}

// Set to true by applyPwaUpdate so `controllerchange` only reloads when
// the user consented to the refresh, not on first-time SW install.
let userTriggeredUpdate = false;

/**
 * Called by the update toast. Tells the waiting SW to activate
 * immediately; the `controllerchange` listener above then reloads the
 * page.
 */
export function applyPwaUpdate(reg) {
  userTriggeredUpdate = true;
  const waiting = reg && reg.waiting;
  if (!waiting) {
    // Fall back to a plain reload if the registration is missing the
    // waiting worker (e.g. user cleared caches between events).
    window.location.reload();
    return;
  }
  waiting.postMessage({ type: 'SKIP_WAITING' });
}
